import { useState } from 'react';
import { useQuery } from 'react-query';

import {
  getCommunityBrochureAndDocs,
  getContractBenefitCategories,
  getContractBenefits,
  getFees,
  getFeesIncentives,
  getLivingInfo,
  getOccupancies,
} from 'apis/CommunityAPI';
import { getCommunityShopNotes } from 'apis/ShopNotesAPI';
import { isManagedBySuperuser } from 'helpers/permissions';
import { CommunityContractBenefit } from 'types/contracts';
import { FeeType } from 'types/fees';
import { LivingType } from 'types/livingType';
import { SnapshotDataType } from 'types/snapshot';
import { isOverallApartmentType } from 'utils/data/apartmentTypes';
import { arrayToDict, groupArray } from 'utils/helpers/array';
import { fetchFiles } from 'utils/helpers/files';
import { isNonNegativeNumber } from 'utils/helpers/numbers';

import {
  getApartmentTypeGroupSlug,
  getApartmentTypeSlug,
  getContractsSlug,
  sortAccommodationEntriesByBaseRent,
} from './accommodations/helpers';
import {
  FEES_FIELD_COMMUNITY_FEE_EQUIVALENT_ENABLED,
  FEES_FIELD_FREQUENCY_PERCENTAGE,
  FEES_FIELDS_KEYS,
  FEES_LEVELS_FIELDS_KEYS,
  FIELD_CATEGORY,
  VACANCY_TYPE,
} from './constants';
import { getSnapshotBoundOccupancies } from './helpers';
import { SnapshotShopNoteType } from './types';
import { SNAPSHOT_SELECTOR_SELECTION } from '../../common/Snapshot/editor/constants';
import { APARTMENT_TYPE, FILE_SOURCE } from '../../constants';
import { useAppSelector } from '../../store/hook';
import { useRootContext } from '../layout/RootContext';

export const useSnapshotData = (community: number, snapshot: SnapshotDataType | undefined, mode: number) => {
  const livingTypes = useAppSelector((state) => state.global.livingTypes);
  const { currentUser } = useAppSelector((state) => state.auth);

  const { showMessage } = useRootContext();
  const [transformedData, setTransformedData] = useState<any>(undefined);

  const livingTypesAbbreviationMap = Object.fromEntries(livingTypes.map((item) => [item.id, item.abv]));
  const fetchMultipleData = async (communityId: number, snapshot: SnapshotDataType | undefined) => {
    // Prevent initial data loading and provide blank slate for entering new data if user is superuser, staff, manager or entering historical data
    // When entering historical data
    // When shoppers are adding data (currently identified as superuser, staff or manager status)
    const isEnterNewData = mode === SNAPSHOT_SELECTOR_SELECTION.ENTER_NEW_DATA;
    const isEnterHistoricalData = mode === SNAPSHOT_SELECTOR_SELECTION.ENTER_HISTORICAL_DATA;
    const isEnterDraftData = mode === SNAPSHOT_SELECTOR_SELECTION.ENTER_SHOP_DATA;
    const isSystemDataUser =
      currentUser?.isSuperuser ||
      currentUser?.isStaff ||
      isManagedBySuperuser(currentUser) ||
      !(currentUser?.company || currentUser?.holding);

    const skipDataLoad =
      (isSystemDataUser && isEnterNewData) || isEnterHistoricalData || (isEnterDraftData && !snapshot);

    if (skipDataLoad) {
      const contractBenefitCategories = await getContractBenefitCategories();
      return {
        accommodations: [],
        feesIncentives: [],
        fees: [],
        occupancies: [],
        documents: [],
        shopNotes: [],
        contractBenefits: [],
        contractBenefitCategories: contractBenefitCategories,
      };
    }

    const [
      accommodations,
      feesIncentives,
      fees,
      occupancies,
      documents,
      shopNotes,
      contractBenefits,
      contractBenefitCategories,
    ] = await Promise.all([
      getLivingInfo(communityId, { snapshot: snapshot?.uuid }),
      getFeesIncentives(communityId, { snapshot: snapshot?.uuid }),
      getFees(communityId, { snapshot: snapshot?.uuid }),
      getOccupancies(communityId, {
        snapshot: snapshot?.uuid,
      }),
      getCommunityBrochureAndDocs(communityId, {
        source: [FILE_SOURCE.MEDIA],
        snapshot: snapshot?.uuid,
      }),
      getCommunityShopNotes(communityId, { snapshot: snapshot?.uuid }),
      getContractBenefits(communityId, { community_id: [community], snapshot: snapshot?.uuid }),
      getContractBenefitCategories(),
    ]);

    return {
      accommodations,
      feesIncentives,
      fees,
      occupancies,
      documents,
      shopNotes,
      contractBenefits,
      contractBenefitCategories,
    };
  };

  const { isLoading, isFetching, refetch } = useQuery({
    // The editor payload depends on the selected snapshot; keep cache entries separate per snapshot.
    queryKey: ['community', 'snapshot', 'data', mode, community, snapshot?.uuid],
    queryFn: () => fetchMultipleData(community, snapshot),
    onError: () => {
      showMessage('error', 'Failed to fetch community data.');
    },
    onSuccess: async (data: any) => {
      // Accommodations
      // Ensuring data is in proper hierarchy for editing accommodations
      // {LivingType}.{ApartmentTypeGroup}.{ApartmentType}
      const filteredAccommodations = (data?.accommodations ?? []).filter(
        (item: any) => item.apartment_type_groups?.length > 0
      );

      filteredAccommodations.forEach((item: any) => {
        const itemMainGroup = item.apartment_type_groups[0];
        item['living_type_abv'] = livingTypesAbbreviationMap[item.living_type_id];
        item['contracts_slug'] = getContractsSlug(item?.contracts);
        item['apartment_type_id_slug'] = getApartmentTypeSlug(item.apartment_type_id);
        item['apartment_type_group_id'] = itemMainGroup.id;
        item['apartment_type_group_id_slug'] = getApartmentTypeGroupSlug(itemMainGroup.id);
        item['apartment_type_group'] = itemMainGroup;
        item['unit_type'] = item.unit_type ? item.unit_type.name : undefined;
      });

      const accommodationsGrouped = groupArray(filteredAccommodations, 'living_type_abv');
      Object.keys(accommodationsGrouped).forEach((key: any) => {
        accommodationsGrouped[key] = groupArray(accommodationsGrouped[key], 'contracts_slug');
      });

      Object.keys(accommodationsGrouped).forEach((primaryKey: any) => {
        Object.keys(accommodationsGrouped[primaryKey]).forEach((secondKey: any) => {
          accommodationsGrouped[primaryKey][secondKey] = groupArray(
            accommodationsGrouped[primaryKey][secondKey],
            'apartment_type_group_id_slug'
          );
        });
      });

      Object.keys(accommodationsGrouped).forEach((primaryKey: any) => {
        Object.keys(accommodationsGrouped[primaryKey]).forEach((secondKey: any) => {
          Object.keys(accommodationsGrouped[primaryKey][secondKey]).forEach((thirdKey: any) => {
            const sortedEntries = sortAccommodationEntriesByBaseRent(
              accommodationsGrouped[primaryKey][secondKey][thirdKey].map((item: any) => [
                item.apartment_type_id_slug,
                item,
              ])
            );

            accommodationsGrouped[primaryKey][secondKey][thirdKey] = arrayToDict(
              sortedEntries.map(([, item]) => item),
              'apartment_type_id_slug'
            );
          });
        });
      });

      // Occupancies
      const snapshotBoundOccupancies = getSnapshotBoundOccupancies(data?.occupancies ?? [], snapshot);
      const transformedOccupancies = snapshotBoundOccupancies.map((item: any) => {
        const livingType = livingTypes.find((livingType: LivingType) => livingType.id === item.living_type_id);

        item['living_type_abv'] = livingType?.abv;

        const vacancyType =
          item.total_apartments_units != null && item.free_apartments_units != null
            ? VACANCY_TYPE.UNIT
            : VACANCY_TYPE.PERCENTAGE;
        item['vacancy_type'] = vacancyType;

        if (vacancyType === VACANCY_TYPE.UNIT) {
          item['occupied_apartments_units'] = item.total_apartments_units - item.free_apartments_units;
        }

        if (isOverallApartmentType({ id: item['apartment_type_id'] })) {
          item['apartment_type_id'] = APARTMENT_TYPE.OVERALL;
        }

        if (item['pct_occupancy']) {
          item['pct_occupancy'] = parseFloat(item['pct_occupancy']);
        }

        return item;
      });

      // Separate room-type and overall occupancies
      const [transformedOccupanciesRoomType, transformedOccupanciesOverall] = transformedOccupancies.reduce(
        (acc: any, item: any) => {
          acc[isOverallApartmentType({ id: item.apartment_type_id }) ? 1 : 0].push(item);
          return acc;
        },
        [[], []] as [any[], any[]]
      );

      // Ensure all living types have an entry in both lists
      const ensureLivingTypeEntries = (occupancies: any[], initialValues = {}) => {
        const existingLivingTypeIds = new Set(occupancies.map((item) => item.living_type_id));

        livingTypes.forEach(({ id, abv }) => {
          if (!existingLivingTypeIds.has(id)) {
            occupancies.push({ living_type_id: id, living_type_abv: abv, ...initialValues });
          }
        });
      };

      ensureLivingTypeEntries(transformedOccupanciesRoomType);
      ensureLivingTypeEntries(transformedOccupanciesOverall, { apartment_type_id: APARTMENT_TYPE.OVERALL });

      const occupancies = groupArray(transformedOccupanciesRoomType, 'living_type_abv');
      const occupanciesOverall = groupArray(transformedOccupanciesOverall, 'living_type_abv');

      // Fees Incentives
      const transformedFeesIncentives = data?.feesIncentives.map((item: any) => {
        const filteredItem = Object.keys(item).reduce((acc, key) => {
          if (isNonNegativeNumber(item[key])) acc[key] = item[key];
          return acc;
        }, {} as any);

        return {
          ...filteredItem,
          contracts_slug: getContractsSlug(item?.contracts),
          living_type_abv: livingTypesAbbreviationMap[item.living_type_id],
          [FEES_FIELD_COMMUNITY_FEE_EQUIVALENT_ENABLED]:
            !isNonNegativeNumber(item.communityFee) && isNonNegativeNumber(item.frequency),
          [FEES_FIELD_FREQUENCY_PERCENTAGE]: Math.round((item?.frequency ?? 0) * 100),
        };
      });

      const feesIncentivesGrouped = groupArray(transformedFeesIncentives ?? [], 'living_type_abv');
      Object.keys(feesIncentivesGrouped).forEach((key: any) => {
        feesIncentivesGrouped[key] = arrayToDict(feesIncentivesGrouped[key], 'contracts_slug');
      });

      Object.keys(feesIncentivesGrouped).forEach((livingTypeKey) => {
        Object.keys(feesIncentivesGrouped[livingTypeKey]).forEach((contractKey) => {
          const currentFeesContractData = feesIncentivesGrouped[livingTypeKey][contractKey];
          feesIncentivesGrouped[livingTypeKey][contractKey]['fields'] = Object.keys(currentFeesContractData).filter(
            (key: string) =>
              currentFeesContractData !== undefined &&
              isNonNegativeNumber(currentFeesContractData[key]) &&
              FEES_FIELDS_KEYS.includes(key)
          );
        });
      });

      // Fees
      const transformedFees = data?.fees.map((item: any) => {
        const filteredItem = Object.fromEntries(
          Object.entries(item).filter(([key, value]) => typeof value !== 'number' || isNonNegativeNumber(value))
        );

        return {
          ...filteredItem,
          contracts_slug: getContractsSlug(item?.contracts),
          living_type_abv: livingTypesAbbreviationMap[item.living_type_id],
        };
      });

      const feesGrouped: Record<string, Record<string, Partial<Record<FeeType, any>>>> = {};
      (transformedFees ?? []).forEach((item: any) => {
        const livingTypeKey = item?.living_type_abv;
        const contractKey = item?.contracts_slug;
        const feeType = (item?.fee_type ?? FeeType.CARE_FEE) as FeeType;

        if (!livingTypeKey || !contractKey) {
          return;
        }

        if (!feesGrouped[livingTypeKey]) {
          feesGrouped[livingTypeKey] = {};
        }

        if (!feesGrouped[livingTypeKey][contractKey]) {
          feesGrouped[livingTypeKey][contractKey] = {};
        }

        const populatedFields = Object.keys(item).filter(
          (key: string) => item !== undefined && isNonNegativeNumber(item[key]) && FEES_LEVELS_FIELDS_KEYS.includes(key)
        );

        const contractFeesByType = feesGrouped[livingTypeKey][contractKey] as Partial<Record<FeeType, any>>;

        contractFeesByType[feeType] = {
          ...item,
          fields: populatedFields,
        };
      });

      // Shop Notes
      // In case we are editing existing Snapshot data load up ShopNote and related files.
      const shopNotes = snapshot
        ? await Promise.all(
            data?.shopNotes.map(async (item: SnapshotShopNoteType) => {
              const payload = { ...item };
              if (item?.attachment_url) {
                const filename = item?.attachment_url.split('/').pop();
                const extension = filename?.split('.').pop();
                const name = filename?.substring(0, filename.lastIndexOf('.'));

                payload['attachment'] = await fetchFiles([
                  {
                    id: item.id,
                    file_path: item.attachment_url,
                    file_name: filename,
                    file_extension: extension,
                  },
                ]);
              }

              return payload;
            })
          )
        : [];

      // Documents and Brochures
      // In case we are editing existing Snapshot data load up files.
      const documents = snapshot ? await fetchFiles(data?.documents) : [];

      // Contract Benefits
      const benefitsGrouped = (data?.contractBenefits ?? []).reduce(
        (acc: any, benefit: any) => {
          const contractId = benefit.contract_id ?? '';
          if (!acc[contractId]) {
            acc[contractId] = [];
          }
          acc[contractId].push(benefit);
          return acc;
        },
        {} as Record<string | number, CommunityContractBenefit[]>
      );

      setTransformedData((prevState: any) => ({
        ...prevState,
        [FIELD_CATEGORY.ACCOMMODATIONS]: accommodationsGrouped,
        [FIELD_CATEGORY.FEES_INCENTIVES]: feesIncentivesGrouped,
        [FIELD_CATEGORY.FEES]: feesGrouped,
        [FIELD_CATEGORY.OCCUPANCIES]: occupancies,
        [FIELD_CATEGORY.OCCUPANCIES_OVERALL]: occupanciesOverall,
        [FIELD_CATEGORY.DOCUMENTS]: documents,
        [FIELD_CATEGORY.SHOP_NOTES]: shopNotes.length ? shopNotes : [{}],
        [FIELD_CATEGORY.CONTRACT_BENEFITS]: benefitsGrouped || [],
        [FIELD_CATEGORY.CONTRACT_BENEFIT_CATEGORIES]: data?.contractBenefitCategories || [],
      }));
    },
    enabled: Boolean(community),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  return {
    data: transformedData,
    loading: isLoading || isFetching,
    refetch,
  };
};
